import { useState, type FC } from "react";
import { useClient } from "../../graphql/hooks/useClient";
import { HorizontalSpacer } from "../atoms/Spacers";
import Card from "../atoms/Card";
import { Col, Row } from "react-grid-system";
import InCardSectionTitle from "./InCardSectionTitle";
import type { RiskScoreClass } from "../../models/RiskScoreModel";
import Subtitle from "../atoms/typography/Subtitle";
import Alert from "./Alert";
import LoadingSpinner from "../atoms/LoadingSpinner";
import { useTranslation } from "react-i18next";
import Body from "../atoms/typography/Body";
import styled from "styled-components";
import { theme } from "../../theme";
import { CopyIcon } from "../atoms/icons/CopyIcon";
import { CheckmarkIconSmall } from "../atoms/icons/CheckmarkIcons";
import { AmlIcon } from "../atoms/icons/AmlIcon";
import XS from "../atoms/typography/XS";
import Small from "../atoms/typography/Small";

interface Props {
  clientIdentifier: string;
}

const AmlStatusBadgeContainer = styled.div<{ $color: string }>`
  border-radius: 4px;
  border: 1px dashed ${(props) => props.$color};
  padding: 2px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: white;
`;

export const getAmlStatusBadge = (
  score?: number,
  riskClass?: RiskScoreClass,
  isManuallyEdited?: boolean,
) => {
  switch (riskClass) {
    case "HIGH":
      return (
        <AmlStatusBadgeContainer $color={theme.colors.error}>
          <AmlIcon color={theme.colors.error} />
          <div>
            <XS color={theme.colors.textMuted}>AML Risk</XS>
            <Small color={theme.colors.error}>
              Hög - {score}
              {isManuallyEdited ? "*" : ""}p
            </Small>
          </div>
        </AmlStatusBadgeContainer>
      );
    case "MEDIUM":
      return (
        <AmlStatusBadgeContainer $color={theme.colors.warning}>
          <AmlIcon color={theme.colors.warning} />
          <div>
            <XS color={theme.colors.textMuted}>AML Risk</XS>
            <Small color={theme.colors.warning}>
              Förhöjd - {score}
              {isManuallyEdited ? "*" : ""}p
            </Small>
          </div>
        </AmlStatusBadgeContainer>
      );
    case "NORMAL":
      return (
        <AmlStatusBadgeContainer $color={theme.colors.success}>
          <AmlIcon color={theme.colors.success} />
          <div>
            <XS color={theme.colors.textMuted}>AML Risk</XS>
            <Small color={theme.colors.success}>
              Normal - {score}
              {isManuallyEdited ? "*" : ""}p
            </Small>
          </div>
        </AmlStatusBadgeContainer>
      );
    default:
      return (
        <AmlStatusBadgeContainer $color={theme.colors.disabled}>
          <AmlIcon color={theme.colors.disabled} />
          <div>
            <XS color={theme.colors.textMuted}>AML Risk</XS>
            <Small color={theme.colors.disabled}>
              {score}
              {isManuallyEdited ? "*" : ""}p
            </Small>
          </div>
        </AmlStatusBadgeContainer>
      );
  }
};

const FlexedDivButton = styled.button`
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid transparent;
  width: fit-content;
  outline: none;
  border: none;
  background-color: transparent;

  &:hover {
    cursor: pointer;
    border-bottom: 1px solid ${theme.colors.primary};
  }

  &:active {
    border-bottom: 1px solid ${theme.colors.primary};
  }
`;

const ClientCardHeader: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const { client, error, loading } = useClient(clientIdentifier);
  const [copied, setCopied] = useState(false);

  const onClickCopyClientIdentifier = () => {
    navigator.clipboard.writeText(clientIdentifier);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <HorizontalSpacer spacing={1} />
      {loading && <LoadingSpinner />}
      {error && (
        <Alert
          title={t("errors.somethingWentWrong")}
          message={error?.toString()}
          type="error"
        />
      )}
      <Card spacing={2}>
        <Subtitle>{client?.name}</Subtitle>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col lg={3}>
            <InCardSectionTitle
              title={t("common.organisationNumber")}
              nodeContent={
                <FlexedDivButton onClick={onClickCopyClientIdentifier}>
                  <Body>{clientIdentifier}</Body>
                  {copied ? (
                    <CheckmarkIconSmall color={theme.colors.textMuted} />
                  ) : (
                    <CopyIcon color={theme.colors.textMuted} />
                  )}
                </FlexedDivButton>
              }
            />
          </Col>
          <Col lg={3}>
            <InCardSectionTitle
              title={t("clientCard.aml")}
              status={getAmlStatusBadge(
                client?.riskScore?.current?.totalScore,
                client?.riskScore?.current?.riskScoreClass,
                client?.riskScore?.currentManualRiskScore !== null,
              )}
            />
          </Col>
          <Col lg={3}>
            <InCardSectionTitle
              title={t("clientCard.integration")}
              content={client?.settings?.accounting?.accountingSystem ?? "-"}
            />
          </Col>
          <Col lg={3}>
            <InCardSectionTitle
              title={t("clientCard.autobook")}
              content={
                client?.settings?.accounting?.autobookActive
                  ? t("common.active")
                  : t("common.inactive")
              }
            />
          </Col>
        </Row>
      </Card>
      <HorizontalSpacer spacing={1} />
    </div>
  );
};

export default ClientCardHeader;
